import { DownloadCloud, FlaskConical, ScanSearch } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type { AttributeFetch } from "../../types/flows";
import { ErrorBanner, fmtRelative, Spinner } from "../flows/ui";

interface Status {
	specs: number;
	minSpecs: number;
	fetch: AttributeFetch | null;
}

interface Preview {
	productId: number;
	name: string;
	existing: Record<string, string>;
	found: Record<string, string>;
	pagesTried: number;
	pagesUsed: number;
	crawl: Record<string, unknown> | null;
	error: string | null;
}

const statusLine = (fetch: AttributeFetch | null): string => {
	if (!fetch) return "Never fetched.";
	if (fetch.status === "queued")
		return `Queued ${fmtRelative(fetch.requestedAt)}.`;
	if (fetch.status === "running") return "Crawling the shops now.";
	if (fetch.status === "failed")
		return `Failed ${fmtRelative(fetch.finishedAt)}: ${fetch.error ?? "no reason given"}.`;
	return `Fetched ${fmtRelative(fetch.finishedAt)}: ${
		fetch.added.length > 0
			? `added ${fetch.added.join(", ")}`
			: "nothing new on the shop pages"
	}.`;
};

const smallButton =
	"inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50";
const smallPrimary =
	"inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-brand-hover disabled:opacity-50";

export const AttributeCrawler = ({
	productId,
	onSaved,
}: {
	productId: number;
	onSaved?: () => void;
}) => {
	const [status, setStatus] = useState<Status | null>(null);
	const [preview, setPreview] = useState<Preview | null>(null);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState<"preview" | "fetch" | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setError(null);
		try {
			const next = await fetchJson<Status>(`/flows/attributes/${productId}`);
			setStatus((previous) => {
				const was = previous?.fetch?.status;
				if (
					(was === "queued" || was === "running") &&
					next.fetch?.status === "done" &&
					next.fetch.added.length > 0
				)
					onSaved?.();
				return next;
			});
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [productId, onSaved]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		const state = status?.fetch?.status;
		if (state !== "queued" && state !== "running") return;
		const timer = window.setTimeout(load, 5000);
		return () => window.clearTimeout(timer);
	}, [status, load]);

	const runPreview = async () => {
		setBusy("preview");
		setError(null);
		setPreview(null);
		try {
			const result = await sendJson<Partial<Preview>>(
				`/flows/attributes/${productId}/preview`,
				"POST",
			);
			if (!result.found)
				setError(result.error ?? "The dry run returned nothing.");
			else setPreview(result as Preview);
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(null);
		}
	};

	const runFetch = async () => {
		setBusy("fetch");
		setError(null);
		try {
			const result = await sendJson<{
				queued?: number[];
				skipped?: Array<{ reason: string }>;
				error?: string;
			}>(`/flows/attributes/${productId}/fetch`, "POST");
			if (result.error) setError(result.error);
			else if (!result.queued?.length)
				setError(
					`Not queued: ${(result.skipped ?? []).map((s) => s.reason).join(", ") || "unknown reason"}.`,
				);
			setPreview(null);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(null);
		}
	};

	const found = Object.entries(preview?.found ?? {});
	const thin = status ? status.specs < status.minSpecs : false;

	return (
		<div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
			<div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
				<ScanSearch className="h-4 w-4 text-slate-400" />
				<h2 className="text-sm font-semibold text-slate-700">
					Attribute crawler
				</h2>
				{status && (
					<span
						className={`ml-1 text-xs font-normal ${thin ? "text-amber-700" : "text-slate-400"}`}
					>
						({status.specs} spec{status.specs === 1 ? "" : "s"})
					</span>
				)}
				<div className="ml-auto flex items-center gap-2">
					<button
						type="button"
						disabled={busy !== null}
						onClick={runPreview}
						title="Crawl the shop pages now and show what would be added. Nothing is written."
						className={smallButton}
					>
						<FlaskConical className="h-3.5 w-3.5" />
						{busy === "preview" ? "Crawling…" : "Test (dry run)"}
					</button>
					<button
						type="button"
						disabled={busy !== null}
						onClick={runFetch}
						title="Queue the product for the attribute pass, which fills the attributes it lacks. Existing values are never overwritten."
						className={smallPrimary}
					>
						<DownloadCloud className="h-3.5 w-3.5" /> Fetch & save
					</button>
				</div>
			</div>

			<div className="space-y-3 p-6">
				<ErrorBanner error={error} />
				{loading ? (
					<Spinner />
				) : (
					<p className="text-[12px] text-slate-600">
						{statusLine(status?.fetch ?? null)}
						{thin &&
							` A comparison page needs ${status?.minSpecs} specs per product; until then its pairs wait for a fetch.`}
					</p>
				)}

				{busy === "preview" && (
					<p className="text-[12px] text-slate-500">
						Crawling up to four shop pages, one request per shop, then asking
						the model for the attributes. This takes a few seconds to a minute.
					</p>
				)}

				{preview && (
					<div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
						<p className="text-[12px] text-slate-600">
							Dry run: {preview.pagesUsed} of {preview.pagesTried} shop pages
							usable
							{preview.error ? ` · ${preview.error}` : ""}
							{typeof preview.crawl?.last_error === "string"
								? ` · last error ${preview.crawl.last_error}`
								: ""}
						</p>
						{found.length === 0 ? (
							<p className="text-[12px] text-slate-500">
								Nothing would be added.
							</p>
						) : (
							<div className="grid grid-cols-2 gap-2 md:grid-cols-3">
								{found.map(([key, value]) => (
									<div
										key={key}
										className="rounded-lg border border-emerald-100 bg-white px-3 py-2"
									>
										<p className="text-[11px] text-emerald-700">+ {key}</p>
										<p className="text-sm font-medium text-slate-700">
											{value}
										</p>
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
